/**
 * PLANLAMA — komuta merkezi. Aksiyon Planı + İçerik Takvimi TEK sayfada:
 *  - Üstte ajan durum şeridi + uzman kararı + aksiyon kartları (kompakt ızgara).
 *  - Altta takvim: kartlar mini (ekran şişmesin), gün hücresine sürükle-bırak çalışır.
 *  - "Takvime Yerleştir": plandaki maddeleri SEO-GEO düzeninde otomatik dağıtır
 *    (kritik önce, günde en çok 2 iş, içerikler yayın günlerine Pzt/Perş, görevler hafta içine).
 *  - İçerik ekleme MANUEL DEĞİL: kartlar içerik ajanından ve aksiyon planından gelir.
 * Eski /action-plan ve /content-calendar rotaları buraya yönlenir.
 */
import { confirmCost } from "@/components/cost-confirm";
import { confirmDialog } from "@/components/confirm-dialog";
import { assistantStore } from "@/lib/assistant-store";
import { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, Link } from "wouter";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  useDraggable, useDroppable, pointerWithin, type DragEndEvent, type DragStartEvent,
} from "@dnd-kit/core";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  CalendarDays, Plus, ChevronLeft, ChevronRight, Trash2, X, AlertTriangle,
  ListChecks, GripVertical, ExternalLink, FileText, RefreshCw, Archive, ClipboardCheck,
  Crosshair, ArrowRight, Loader2, Zap, TrendingDown, Wrench, Sparkles, Pencil, Check,
  Radar, Settings, Link2, Globe, Send, Wand2, Eraser, BrainCircuit, Bot,
} from "lucide-react";

/* ───────────────────────────── ortak tipler / sabitler ───────────────────────────── */

interface ContentKeyword { keyword: string; usa: number; global: number; role: string; }
type ContentPlan = {
  id: number; domainId: number; title: string; contentType: string; status: string;
  author: string | null; category: string | null; plannedDate: string; deliveryDate: string | null;
  finalLink: string | null; coverUrl?: string | null; keywords: ContentKeyword[]; notes: string | null;
  sourceUrl: string | null; anchorText: string | null; actionItemId?: string | null; agentKind?: string | null;
  briefId?: number | null; wpYayinModu?: "onayli" | "otomatik" | null;
  wpYayinDurumu?: "bekliyor" | "onay-bekliyor" | "yayinlandi" | "hata" | null; wpYayinNotu?: string | null;
};

const STATUS: Record<string, { label: string; color: string }> = {
  planned:   { label: "Planlandı",  color: "#70706c" },
  writing:   { label: "Yazılıyor",  color: "#f79009" },
  review:    { label: "İncelemede", color: "#ec6461" },
  revise:    { label: "Revize",     color: "#f18e8b" },
  completed: { label: "Tamamlandı", color: "#12b76a" },
  published: { label: "Yayında",    color: "#16a34a" },
};
const STATUS_KEYS = ["planned", "writing", "review", "revise", "completed", "published"];
const DONE = new Set(["completed", "published"]);
// Görev kartlarında içerik durumları anlamsız — sade üçlü akış
const GOREV_STATUS: [string, string][] = [["planned", "Planlandı"], ["writing", "Devam ediyor"], ["completed", "Tamamlandı"]];

const TYPE_STYLE: Record<string, { accent: string; tint: string; border: "solid" | "dashed"; icon: typeof FileText }> = {
  "Yeni blog": { accent: "#12b76a", tint: "transparent", border: "solid", icon: FileText },
  "Revize":    { accent: "#f18e8b", tint: "rgba(236,72,153,0.07)", border: "dashed", icon: RefreshCw },
  "Yedek":     { accent: "#a3a3a0", tint: "rgba(148,163,184,0.07)", border: "dashed", icon: Archive },
  // Aksiyon planından takvime düşen içerik-dışı işler (teknik/link/GEO görevleri)
  "Görev":     { accent: "#70706c", tint: "rgba(112,112,108,0.08)", border: "solid", icon: ClipboardCheck },
  // Planlı tarihte İLGİLİ AJANI OTOMATİK ÇALIŞTIRAN kart (sabah 06:00 cron'u)
  "Ajan koşusu": { accent: "#d97706", tint: "rgba(217,119,6,0.07)", border: "dashed", icon: Bot },
};
const typeOf = (t: string) => TYPE_STYLE[t] ?? TYPE_STYLE["Yeni blog"];
const TYPES = ["Yeni blog", "Revize", "Yedek", "Görev"];

const TR_MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const TR_DAYS = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtK = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));
const volOf = (p: ContentPlan) => (p.keywords ?? []).reduce((s, k) => s + (k.global || 0), 0);

// Aylık içerik kapasitesi — paket sistemi gelene kadar sabit (F2'de kullanıcı paketine bağlanacak)
const MONTHLY_CAPACITY = 20;

type ActionItem = { id?: string; priority: "kritik" | "yuksek" | "orta"; category: string; title: string; why: string; impact: string; effort: string; path: string; steps: string[]; done?: boolean; source?: string; dueDate?: string; calendarPlanId?: number };
type ActionPlanData = { generatedAt: string; cost: number; verdict: string; summary: string; items: ActionItem[]; signals: { label: string; value: string }[] };

const PRI: Record<string, { label: string; color: string; bg: string; order: number }> = {
  kritik: { label: "KRİTİK", color: "#d92d20", bg: "#fef2f2", order: 0 },
  yuksek: { label: "YÜKSEK", color: "#d97706", bg: "#fffbeb", order: 1 },
  orta: { label: "ORTA", color: "#2563eb", bg: "#eff6ff", order: 2 },
};
const CAT: Record<string, { label: string; icon: typeof Zap; color: string }> = {
  "link-insasi": { label: "Link İnşası", icon: Zap, color: "#e83e3c" },
  trafik: { label: "Trafik", icon: TrendingDown, color: "#0891b2" },
  teknik: { label: "Teknik", icon: Wrench, color: "#70706c" },
  geo: { label: "GEO / AI", icon: Sparkles, color: "#d97706" },
  icerik: { label: "İçerik", icon: FileText, color: "#16a34a" },
};
const EFFORT: Record<string, string> = { dusuk: "Düşük efor", orta: "Orta efor", yuksek: "Yüksek efor" };
const SRC: Record<string, string> = { manuel: "elle", asistan: "asistanla" };

type FormState = { title: string; why: string; priority: string; category: string; effort: string; path: string };
const EMPTY_FORM: FormState = { title: "", why: "", priority: "yuksek", category: "icerik", effort: "orta", path: "" };

// Takvime sürüklenen ajan koşusu çipleri — tarih gelince 06:00'da otomatik çalışır
const AGENT_CHIPS: { kind: string; label: string; tokens: number }[] = [
  { kind: "tech-audit", label: "Teknik denetim", tokens: 150 },
  { kind: "backlink", label: "Backlink denetimi", tokens: 200 },
  { kind: "ai-visibility", label: "AI görünürlük", tokens: 750 },
  { kind: "rank-tracking", label: "Sıralama koşusu", tokens: 300 },
  { kind: "seo-report", label: "SEO-GEO raporu", tokens: 100 },
];

function DraggableAgentChip({ c }: { c: (typeof AGENT_CHIPS)[number] }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `agentkind-${c.kind}` });
  return (
    <span ref={setNodeRef} {...attributes} {...listeners}
      className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-full border cursor-grab active:cursor-grabbing touch-none select-none"
      style={{ borderColor: "#d9770655", borderStyle: "dashed", background: "rgba(217,119,6,0.06)", opacity: isDragging ? 0.4 : 1 }}>
      <Bot className="w-3.5 h-3.5" style={{ color: "#d97706" }} />
      <span className="font-medium">{c.label}</span>
      <span className="text-muted-foreground">~{c.tokens}</span>
    </span>
  );
}

/* ───────────────────────────── ajan durum şeridi ───────────────────────────── */

function daysAgo(d: string | Date | null | undefined): number | null {
  if (!d) return null;
  const t = new Date(d).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}
function agoLabel(days: number | null): string {
  if (days === null) return "hiç koşmadı";
  if (days === 0) return "bugün";
  if (days === 1) return "dün";
  return `${days} gün önce`;
}

type ChipState = "running" | "fresh" | "stale" | "none";
function StatusChip({ icon: Icon, label, state, text }: { icon: typeof Zap; label: string; state: ChipState; text: string }) {
  const color = state === "running" ? "#e83e3c" : state === "stale" ? "#d97706" : state === "none" ? "#a3a3a0" : "#12b76a";
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border bg-card" style={{ borderColor: `${color}55` }}>
      <Icon className={`w-3 h-3 ${state === "running" ? "animate-spin" : ""}`} style={{ color }} />
      <span className="font-medium">{label}</span>
      <span style={{ color }}>{text}</span>
    </span>
  );
}

/* ───────────────────────────── takvim mini kartı + gün hücresi ───────────────────────────── */

// KOMPAKT kart: günde 3-4 iş olsa da hücre şişmesin — detay (yazar/kapak/hacim) çekmecede.
function MiniCard({ p, onOpen, handle, todayStr, ghost }: {
  p: ContentPlan; onOpen?: () => void; handle?: Record<string, unknown>; todayStr: string; ghost?: boolean;
}) {
  const st = STATUS[p.status] ?? STATUS.planned;
  const ty = typeOf(p.contentType);
  const TypeIcon = ty.icon;
  const overdue = p.plannedDate < todayStr && !DONE.has(p.status);
  return (
    <div
      onClick={onOpen}
      className="rounded-md px-1.5 py-1 transition-all hover:shadow-sm flex items-center gap-1"
      style={{
        background: ty.tint === "transparent" ? "var(--card)" : ty.tint,
        border: `1px ${ty.border} ${ty.accent}55`,
        borderLeft: `3px solid ${ty.accent}`,
        boxShadow: ghost ? "0 8px 24px rgba(0,0,0,0.25)" : undefined,
        cursor: onOpen ? "pointer" : "grabbing",
      }}
      title={`${p.title} · ${st.label} · ${p.contentType}`}
    >
      <span {...(handle ?? {})} onClick={(e) => e.stopPropagation()}
        className="text-muted-foreground/50 hover:text-muted-foreground cursor-grab active:cursor-grabbing touch-none shrink-0">
        <GripVertical className="w-3 h-3" />
      </span>
      <TypeIcon className="w-3 h-3 shrink-0" style={{ color: ty.accent }} />
      <span className="text-[11px] leading-tight truncate flex-1 min-w-0">
        {overdue && <AlertTriangle className="w-3 h-3 inline mr-0.5 -mt-0.5 text-error-500" />}
        {p.title || "(başlıksız)"}
      </span>
      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: st.color }} title={st.label} />
    </div>
  );
}

function DraggableCard({ p, onOpen, todayStr }: { p: ContentPlan; onOpen: () => void; todayStr: string }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `card-${p.id}` });
  return (
    <div ref={setNodeRef} style={{ opacity: isDragging ? 0.35 : 1 }}>
      <MiniCard p={p} onOpen={onOpen} handle={{ ...attributes, ...listeners }} todayStr={todayStr} />
    </div>
  );
}

function DayCell({ date, items, isToday, dim, onOpen, todayStr }: {
  date: Date | null; items: ContentPlan[]; isToday: boolean; dim: boolean;
  onOpen: (p: ContentPlan) => void; todayStr: string;
}) {
  const ds = date ? ymd(date) : "empty";
  const { setNodeRef, isOver } = useDroppable({ id: `day-${ds}`, disabled: !date });
  if (!date) return <div className="min-h-[92px] rounded-lg bg-muted/10" />;
  return (
    <div ref={setNodeRef}
      className="min-h-[92px] rounded-lg border p-1.5 transition-colors flex flex-col gap-1"
      style={{
        borderColor: isOver ? "#ec6461" : isToday ? "#ec6461" : "var(--border)",
        background: isOver ? "rgba(232,62,60,0.08)" : isToday ? "rgba(232,62,60,0.04)" : "transparent",
        opacity: dim ? 0.5 : 1,
        outline: isOver ? "2px dashed #ec6461" : undefined,
      }}>
      <div className="flex items-center gap-1.5">
        <span className="text-[12px] font-semibold" style={{ color: isToday ? "#ec6461" : "var(--foreground)" }}>{date.getDate()}</span>
        {isToday && <span className="text-[9px] px-1.5 rounded-full bg-brand-500 text-white font-medium">bugün</span>}
      </div>
      {items.map((p) => <DraggableCard key={p.id} p={p} todayStr={todayStr} onOpen={() => onOpen(p)} />)}
    </div>
  );
}

/* ───────────────────────────── aksiyon kartı (üst ızgara, sürüklenebilir) ───────────────────────────── */

function ActionCardBody({ it, first, ghost }: { it: ActionItem; first?: boolean; ghost?: boolean }) {
  const pri = PRI[it.priority] ?? PRI.orta;
  const cat = CAT[it.category] ?? CAT.teknik;
  const Icon = cat.icon;
  return (
    <div className="rounded-lg border p-2.5 bg-card h-full" style={{
      borderColor: first && !it.done ? "rgba(220,38,38,0.4)" : "var(--border)",
      opacity: it.done ? 0.55 : 1,
      boxShadow: ghost ? "0 8px 24px rgba(0,0,0,0.25)" : undefined,
    }}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: pri.bg, color: pri.color }}>{pri.label}</span>
        <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: `${cat.color}18`, color: cat.color }}><Icon className="w-2.5 h-2.5" />{cat.label}</span>
        <span className="text-[9px] text-muted-foreground">{EFFORT[it.effort] ?? it.effort}</span>
        {it.source && SRC[it.source] && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-white/5 dark:text-gray-400">{SRC[it.source]}</span>}
      </div>
      <div className={`text-[13px] font-semibold mt-1 leading-snug line-clamp-2 ${it.done ? "line-through" : ""}`}>{it.title}</div>
      {it.why && !it.done && <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{it.why}</div>}
      {it.dueDate && !it.done && (
        <div className="text-[10px] font-medium mt-1 inline-flex items-center gap-1" style={{ color: "#e83e3c" }}>
          <CalendarDays className="w-3 h-3" /> {(() => { const [, m, dd] = it.dueDate.split("-"); return `${Number(dd)} ${TR_MONTHS[Number(m) - 1]?.slice(0, 3)}`; })()} — takvimde
        </div>
      )}
    </div>
  );
}

function DraggableAction({ it, children }: { it: ActionItem; children: React.ReactNode }) {
  // Tamamlanmış ya da id'siz maddeler sürüklenmez
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `action-${it.id}`, disabled: !it.id || !!it.done });
  return (
    <div ref={setNodeRef} style={{ opacity: isDragging ? 0.35 : 1 }} className="h-full">
      <div className="flex items-start gap-1 h-full">
        <span {...attributes} {...listeners} className="mt-3 text-muted-foreground/50 hover:text-muted-foreground cursor-grab active:cursor-grabbing touch-none shrink-0" title="Takvime sürükle">
          <GripVertical className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0 h-full">{children}</div>
      </div>
    </div>
  );
}

/* ───────────────────────────── sayfa ───────────────────────────── */

export default function Planning() {
  const { data: domains } = useListDomains();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const [activeDomainId] = useActiveDomain(domains);
  const activeDomain = domains?.find((d) => d.id === activeDomainId);

  const today = new Date();
  const todayStr = ymd(today);
  // WordPress yayın kuyruğu: "Yayınla" → kart WP'de hemen yayına alınır
  const [wpYayinBusy, setWpYayinBusy] = useState<number | null>(null);
  const wpSimdiYayinla = async (pid: number) => {
    setWpYayinBusy(pid);
    try {
      const r = await fetch(`/api/content-plans/${pid}/wp-yayinla`, { method: "POST" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "Yayınlanamadı");
      toast({ title: "WordPress'te yayınlandı ✓", description: j.wpLink ?? undefined });
      refetchPlans();
    } catch (e) { toast({ title: "Yayınlanamadı", description: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setWpYayinBusy(null); }
  };
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [tab, setTab] = useState<"calendar" | "list">("calendar");
  const [trackFilter, setTrackFilter] = useState<"publish" | "revise" | "overdue" | null>(null);
  const [dimPast, setDimPast] = useState(true);
  const [drag, setDrag] = useState<{ type: "card"; id: number } | { type: "action"; id: string } | { type: "agent"; kind: string } | null>(null);
  const [placing, setPlacing] = useState(false);
  const [clearing, setClearing] = useState(false);

  /* ── içerik planları ── */
  const plansKey = ["content-plans", activeDomainId];
  const { data: plansData } = useQuery({
    queryKey: plansKey,
    enabled: !!activeDomainId,
    queryFn: async (): Promise<ContentPlan[]> => {
      const r = await fetch(`/api/domains/${activeDomainId}/content-plans`);
      if (!r.ok) return [];
      const j = (await r.json()) as { contentPlans: ContentPlan[] };
      return (j.contentPlans ?? []).map((p) => ({ ...p, keywords: p.keywords ?? [] }));
    },
  });
  const plans = plansData ?? [];
  const refetchPlans = () => queryClient.invalidateQueries({ queryKey: plansKey });

  /* ── aksiyon planı ── */
  const { data: apData } = useQuery({
    queryKey: ["action-plan", activeDomainId],
    enabled: !!activeDomainId,
    refetchInterval: (q) => ((q.state.data as { running?: boolean } | undefined)?.running ? 3000 : false),
    queryFn: async (): Promise<{ plan: ActionPlanData | null; runAt: string | null; running: boolean; runError: string | null }> => {
      const r = await fetch(`/api/domains/${activeDomainId}/action-plan`);
      return r.ok ? r.json() : { plan: null, runAt: null, running: false, runError: null };
    },
  });
  const plan = apData?.plan;
  const planRunning = apData?.running ?? false;
  const refreshPlan = () => queryClient.invalidateQueries({ queryKey: ["action-plan", activeDomainId] });
  const doneCount = plan?.items.filter((i) => i.done).length ?? 0;

  /* ── AI önerileri (F4 refleksleri): pazartesi ritüeli + elle tazeleme üretir ── */
  type Suggestion = { id: number; kind: string; title: string; why: string; category: string; priority: string; suggestedDate: string | null; agentKind: string | null; cardId: number | null };
  const sugKey = ["plan-suggestions", activeDomainId];
  const { data: sugData } = useQuery({
    queryKey: sugKey,
    enabled: !!activeDomainId,
    queryFn: async (): Promise<Suggestion[]> => {
      const r = await fetch(`/api/domains/${activeDomainId}/plan-suggestions`);
      if (!r.ok) return [];
      return ((await r.json()) as { suggestions: Suggestion[] }).suggestions ?? [];
    },
  });
  const suggestions = sugData ?? [];
  const [sugBusy, setSugBusy] = useState<number | null>(null);
  const [sugRefreshing, setSugRefreshing] = useState(false);
  const refetchSugs = () => queryClient.invalidateQueries({ queryKey: sugKey });

  const refreshSuggestions = async () => {
    if (!activeDomainId || sugRefreshing) return;
    setSugRefreshing(true);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/plan-suggestions/refresh`, { method: "POST" });
      const j = (await r.json().catch(() => ({}))) as { created?: number };
      toast({ title: j.created ? `${j.created} yeni öneri üretildi` : "Yeni öneri yok", description: j.created ? undefined : "Sinyallerde öneri gerektiren sapma bulunamadı. 👍" });
      refetchSugs();
    } finally { setSugRefreshing(false); }
  };
  const acceptSuggestion = async (s: Suggestion) => {
    if (!activeDomainId || sugBusy) return;
    setSugBusy(s.id);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/plan-suggestions/${s.id}/accept`, { method: "POST" });
      const j = (await r.json().catch(() => ({}))) as { applied?: string; error?: string };
      if (!r.ok) throw new Error(j.error ?? "Uygulanamadı");
      toast({ title: "Öneri uygulandı", description: j.applied });
      refetchSugs(); refetchPlans(); refreshPlan();
    } catch (e) { toast({ title: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setSugBusy(null); }
  };
  const dismissSuggestion = async (s: Suggestion) => {
    if (!activeDomainId) return;
    await fetch(`/api/domains/${activeDomainId}/plan-suggestions/${s.id}/dismiss`, { method: "POST" }).catch(() => null);
    refetchSugs();
  };

  /* ── ŞEFİN HAFTALIK PLANI (Orkestra Şefi Faz 3): Pzt 08:30 otomatik; elle de üretilebilir ── */
  type AsistanSecimF = { firsatId: number; baslik: string; kaynak: string; etki: number; gerekce: string; onerilenTarih: string; durum: "onerildi" | "onaylandi" | "reddedildi"; planId?: number };
  type AsistanPlanF = { id: number; runAt: string; haftaBasi: string; ozet: string; secimler: AsistanSecimF[]; yapilmayanlar: { baslik: string; neden: string }[] };
  const asistanPlanKey = ["asistan-plani", activeDomainId];
  const { data: asistanPlanVeri } = useQuery({
    queryKey: asistanPlanKey,
    enabled: !!activeDomainId,
    staleTime: 60_000,
    queryFn: async (): Promise<{ plan: AsistanPlanF | null; kartDurumlari?: Record<number, string> }> =>
      (await fetch(`/api/domains/${activeDomainId}/asistan-plani`)).json(),
  });
  const asistanPlan = asistanPlanVeri?.plan ?? null;
  const asistanPlanTaze = !!asistanPlan && Date.now() - new Date(asistanPlan.runAt).getTime() < 7 * 86400_000;
  const { data: asistanAyar } = useQuery<{ seviye: 0 | 1 | 2 }>({
    queryKey: ["asistan-ayarlar", activeDomainId], enabled: !!activeDomainId, staleTime: 300_000,
    queryFn: async () => (await fetch(`/api/domains/${activeDomainId}/asistan-ayarlar`)).json(),
  });
  const ASISTAN_MOD = { 0: "Danışman", 1: "Yarı otomatik", 2: "Otopilot" } as const;
  /* Kazanım Karnesi (Faz 5): şef ne önerdi → ne yapıldı → ne işe yaradı */
  type AsistanKarne = { donem: number; asistan: { onerilen: number; onaylanan: number; reddedilen: number; tamamlanan: number }; havuz: { yapilan: number; sinyalKapanan: number; sinyalSuruyor: number; kendiligindenKapanan: number } };
  const { data: asistanKarne } = useQuery<AsistanKarne>({
    queryKey: ["asistan-karne", activeDomainId], enabled: !!activeDomainId, staleTime: 300_000,
    queryFn: async () => (await fetch(`/api/domains/${activeDomainId}/asistan-karne?donem=30`)).json(),
  });
  const [asistanPlanBusy, setAsistanPlanBusy] = useState<number | "uret" | null>(null);
  const refetchAsistanPlan = () => queryClient.invalidateQueries({ queryKey: asistanPlanKey });
  const asistanPlaniBaslat = async () => {
    if (!activeDomainId || asistanPlanBusy) return;
    setAsistanPlanBusy("uret");
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/asistan-plani/uret`, { method: "POST" });
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      if (!r.ok) throw new Error(j.error ?? "Üretilemedi");
      toast({ title: "Haftalık plan hazır", description: "Geolop Asistanı önceliklerini çıkardı — tek tıkla onaylayabilirsin." });
      refetchAsistanPlan();
    } catch (e) { toast({ title: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setAsistanPlanBusy(null); }
  };
  const asistanKarar = async (s: AsistanSecimF, karar: "onayla" | "reddet") => {
    if (!activeDomainId || !asistanPlan || asistanPlanBusy) return;
    setAsistanPlanBusy(s.firsatId);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/asistan-plani/${asistanPlan.id}/${karar}`, {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ firsatId: s.firsatId }),
      });
      const j = (await r.json().catch(() => ({}))) as { error?: string; tarih?: string };
      if (!r.ok) throw new Error(j.error ?? "İşlem başarısız");
      if (karar === "onayla") { toast({ title: "Takvime eklendi", description: `${j.tarih ?? s.onerilenTarih} gününe kart açıldı.` }); refetchPlans(); }
      refetchAsistanPlan();
    } catch (e) { toast({ title: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setAsistanPlanBusy(null); }
  };

  /* ── ajan durumları (hepsi ücretsiz GET) ── */
  const { data: techStatus } = useQuery({
    queryKey: ["planning-tech", activeDomainId], enabled: !!activeDomainId, staleTime: 60_000,
    queryFn: async () => { const r = await fetch(`/api/domains/${activeDomainId}/tech-audit`); return r.ok ? (r.json() as Promise<{ runAt: string | null; running: boolean; data: { healthScore?: number } | null }>) : null; },
  });
  const { data: blStatus } = useQuery({
    queryKey: ["planning-backlink", activeDomainId], enabled: !!activeDomainId, staleTime: 60_000,
    queryFn: async () => { const r = await fetch(`/api/domains/${activeDomainId}/backlink-audit`); return r.ok ? (r.json() as Promise<{ runAt: string | null; running: boolean }>) : null; },
  });
  const { data: visStatus } = useQuery({
    queryKey: ["planning-vis", activeDomainId], enabled: !!activeDomainId, staleTime: 60_000,
    queryFn: async () => { const r = await fetch(`/api/domains/${activeDomainId}/ai-visibility`); return r.ok ? (r.json() as Promise<{ latest: { runAt: string } | null }>) : null; },
  });
  const { data: rankStatus } = useQuery({
    queryKey: ["planning-rank", activeDomainId], enabled: !!activeDomainId, staleTime: 60_000,
    queryFn: async () => { const r = await fetch(`/api/domains/${activeDomainId}/rank-tracking`); return r.ok ? (r.json() as Promise<{ keywords: { checkedAt: string | null }[] }>) : null; },
  });

  const chips = useMemo(() => {
    const out: { icon: typeof Zap; label: string; state: ChipState; text: string; href: string }[] = [];
    if (activeDomain) {
      // Üretilen DomainStatus tipi güncel değil ("linking"/"clustering" yok) — string üzerinden kontrol
      const st = String(activeDomain.status);
      const crawling = ["crawling", "linking", "clustering", "analyzing"].includes(st);
      out.push({
        icon: Globe, label: "Tarama", href: `/domains/${activeDomain.id}`,
        state: crawling ? "running" : "fresh",
        text: crawling
          ? `sürüyor ${activeDomain.crawledPages ?? 0}/${activeDomain.totalPages ?? "?"}`
          : agoLabel(daysAgo(activeDomain.lastCrawledAt)),
      });
    }
    const mk = (icon: typeof Zap, label: string, href: string, runAt: string | null | undefined, running: boolean, staleDays: number) => {
      const d = daysAgo(runAt);
      out.push({
        icon, label, href,
        state: running ? "running" : d === null ? "none" : d > staleDays ? "stale" : "fresh",
        text: running ? "çalışıyor" : agoLabel(d),
      });
    };
    mk(Settings, "Teknik", "/tech-audit", techStatus?.runAt, !!techStatus?.running, 30);
    mk(Link2, "Backlink", "/backlink-audit", blStatus?.runAt, !!blStatus?.running, 30);
    mk(Sparkles, "AI görünürlük", "/traffic-analysis", visStatus?.latest?.runAt ?? null, false, 14);
    const lastRank = (rankStatus?.keywords ?? []).reduce<string | null>((acc, k) => (k.checkedAt && (!acc || k.checkedAt > acc) ? k.checkedAt : acc), null);
    mk(Radar, "Sıralama", "/rank-tracking", lastRank, false, 3);
    return out;
  }, [activeDomain, techStatus, blStatus, visStatus, rankStatus]);

  /* ── takvim türetmeleri ── */
  const monthPrefix = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}`;
  const stats = useMemo(() => {
    const all = plans.filter((p) => p.plannedDate.startsWith(monthPrefix));
    const content = all.filter((p) => p.contentType !== "Görev");
    const done = all.filter((p) => DONE.has(p.status)).length;
    const progress = all.length ? Math.round((done / all.length) * 100) : 0;
    return { total: all.length, contentCount: content.length, done, progress };
  }, [plans, monthPrefix]);

  // Takip kuyrukları (tüm aylar — takip aya hapsolmaz)
  const queues = useMemo(() => ({
    publish: plans.filter((p) => ["writing", "review", "completed"].includes(p.status)),
    revise: plans.filter((p) => !DONE.has(p.status) && (p.status === "revise" || p.contentType === "Revize")),
    overdue: plans.filter((p) => p.plannedDate < todayStr && !DONE.has(p.status)),
  }), [plans, todayStr]);

  const byDate = useMemo(() => {
    const m = new Map<string, ContentPlan[]>();
    for (const p of plans) { const a = m.get(p.plannedDate) ?? []; a.push(p); m.set(p.plannedDate, a); }
    return m;
  }, [plans]);

  const cells = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const firstDow = (first.getDay() + 6) % 7;
    const dim = new Date(viewYear, viewMonth + 1, 0).getDate();
    const arr: (Date | null)[] = [];
    for (let i = 0; i < firstDow; i++) arr.push(null);
    for (let d = 1; d <= dim; d++) arr.push(new Date(viewYear, viewMonth, d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [viewYear, viewMonth]);

  const prevMonth = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); } else setViewMonth((m) => m - 1); };
  const nextMonth = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); } else setViewMonth((m) => m + 1); };

  /* ── içerik drawer (yalnız mevcut kartlar — manuel içerik ekleme yok) ── */
  const [selected, setSelected] = useState<ContentPlan | null>(null);
  const [draft, setDraft] = useState<ContentPlan | null>(null);
  useEffect(() => { setDraft(selected ? { ...selected } : null); }, [selected]);
  // Yazar/Sorumlu seçenekleri: tanımlı EKİP üyeleri + geçmişte kullanılan adlar
  const { data: teamData } = useQuery({
    queryKey: ["me-team"], staleTime: 300_000,
    queryFn: async () => { const r = await fetch("/api/me/team"); return r.ok ? (r.json() as Promise<{ team: { id: number; name: string; email: string; role: string }[] }>) : { team: [] }; },
  });
  const authors = useMemo(() => [...new Set([
    ...(teamData?.team ?? []).map((m) => m.name),
    ...(plans.map((p) => p.author).filter(Boolean) as string[]),
  ])], [plans, teamData]);
  const categories = useMemo(() => [...new Set(plans.map((p) => p.category).filter(Boolean) as string[])], [plans]);
  const draftVol = draft ? volOf(draft) : 0;

  const save = async (partial: Partial<ContentPlan>) => {
    if (!draft?.id) return;
    setDraft({ ...draft, ...partial } as ContentPlan);
    try { await fetch(`/api/content-plans/${draft.id}`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(partial) }); refetchPlans(); refreshPlan(); }
    catch { toast({ title: "Kaydedilemedi", variant: "destructive" }); }
  };
  const removePlanCard = async () => {
    if (!draft?.id) { setSelected(null); return; }
    try { await fetch(`/api/content-plans/${draft.id}`, { method: "DELETE" }); toast({ title: "Silindi" }); setSelected(null); refetchPlans(); refreshPlan(); }
    catch { toast({ title: "Silinemedi", variant: "destructive" }); }
  };

  /* ── aksiyon CRUD ── */
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    if (!activeDomainId) return;
    if (!(await confirmCost("action-plan"))) return;
    await fetch(`/api/domains/${activeDomainId}/action-plan`, { method: "POST" });
    refreshPlan();
  };
  const toggleDone = async (it: ActionItem) => {
    if (!activeDomainId || !it.id) return;
    await fetch(`/api/domains/${activeDomainId}/action-plan/items/${it.id}`, {
      method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify({ done: !it.done }),
    });
    refreshPlan();
  };
  const removeItem = async (it: ActionItem) => {
    if (!activeDomainId || !it.id) return;
    if (!(await confirmDialog({ title: "Madde plandan silinsin mi?", description: `"${it.title}"`, confirmText: "Sil", danger: true }))) return;
    await fetch(`/api/domains/${activeDomainId}/action-plan/items/${it.id}`, { method: "DELETE" });
    refreshPlan();
  };
  const openAdd = () => { setEditId(null); setForm(EMPTY_FORM); setFormOpen(true); };
  const openEdit = (it: ActionItem) => {
    setEditId(it.id ?? null);
    setForm({ title: it.title, why: it.why, priority: it.priority, category: it.category, effort: it.effort, path: it.path });
    setFormOpen(true);
  };
  const saveForm = async () => {
    if (!activeDomainId || !form.title.trim()) return;
    setSaving(true);
    try {
      if (editId) {
        await fetch(`/api/domains/${activeDomainId}/action-plan/items/${editId}`, {
          method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(form),
        });
      } else {
        await fetch(`/api/domains/${activeDomainId}/action-plan/items`, {
          method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(form),
        });
      }
      setFormOpen(false);
      refreshPlan();
    } finally { setSaving(false); }
  };

  /* ── aksiyon → takvim kartı (sürükle-bırak VE otomatik yerleştirme aynı yolu kullanır).
   * schedule endpoint'i çift yönlü bağ kurar: kart yayınlanınca madde otomatik tamamlanır,
   * kart silinirse madde plana geri döner; madde zaten takvimdeyse kartın tarihi güncellenir. ── */
  const createCardFromAction = async (it: ActionItem, date: string): Promise<boolean> => {
    if (!activeDomainId || !it.id) return false;
    const r = await fetch(`/api/domains/${activeDomainId}/action-plan/items/${it.id}/schedule`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ date }),
    }).catch(() => null);
    return !!r?.ok;
  };

  /* ── TAKVİME YERLEŞTİR: SEO-GEO çalışma düzeni ──
   * Kural seti (F3'te strateji seansı bunu AI ile kişiselleştirecek):
   *  - kritik → yüksek → orta sırasıyla, en erken uygun güne
   *  - günde en fazla 2 iş (ekip boğulmasın)
   *  - içerik işleri yayın günlerine (Pzt & Perş), görevler herhangi bir hafta içi güne
   *  - zaten takvimde olan (aynı başlıklı) ya da tamamlanmış maddeler atlanır
   */
  const autoPlace = async () => {
    if (!plan || !activeDomainId || placing) return;
    // dueDate = madde zaten takvime bağlı; başlık kontrolü köprü öncesi eski kartlar için
    const existingTitles = new Set(plans.map((p) => p.title));
    const todo = plan.items
      .filter((it) => !it.done && it.id && !it.dueDate && !existingTitles.has(it.title))
      .sort((a, b) => (PRI[a.priority]?.order ?? 2) - (PRI[b.priority]?.order ?? 2));
    if (todo.length === 0) {
      toast({ title: "Yerleştirilecek yeni madde yok", description: "Maddelerin tümü zaten takvimde ya da tamamlanmış." });
      return;
    }
    if (!(await confirmDialog({
      title: `${todo.length} madde takvime yerleştirilecek`,
      description: "SEO-GEO çalışma düzenine göre otomatik dağıtılır:",
      lines: ["Kritik işler en öne, günde en fazla 2 iş", "İçerikler yayın günlerine (Pzt/Perş)", "Görevler hafta içine, hafta sonu boş"],
      confirmText: "Takvime Yerleştir",
    }))) return;

    setPlacing(true);
    try {
      // Gün doluluğu: mevcut açık kartlar + bu turda atananlar
      const counts = new Map<string, number>();
      for (const p of plans) if (!DONE.has(p.status)) counts.set(p.plannedDate, (counts.get(p.plannedDate) ?? 0) + 1);
      const start = new Date(); start.setDate(start.getDate() + 1);
      const slotFor = (contentJob: boolean): string => {
        const d = new Date(start);
        for (let i = 0; i < 90; i++) {
          const dow = d.getDay(); // 0 Paz … 6 Cmt
          const okDay = contentJob ? dow === 1 || dow === 4 : dow >= 1 && dow <= 5;
          const ds = ymd(d);
          if (okDay && (counts.get(ds) ?? 0) < 2) return ds;
          d.setDate(d.getDate() + 1);
        }
        return ymd(start);
      };
      let placed = 0;
      for (const it of todo) {
        const ds = slotFor(it.category === "icerik");
        if (await createCardFromAction(it, ds)) { counts.set(ds, (counts.get(ds) ?? 0) + 1); placed++; }
      }
      toast({ title: `${placed} madde takvime yerleştirildi`, description: "Kritik işler öne alındı; içerikler Pzt/Perş yayın günlerinde." });
      refetchPlans(); refreshPlan();
    } finally { setPlacing(false); }
  };

  /* ── TAKVİMİ TEMİZLE: açık kartları siler; yayında/tamamlananlara dokunmaz.
   * Silinen kart aksiyon planından geldiyse madde plana geri döner (unlink sync). ── */
  const clearCalendar = async () => {
    if (clearing) return;
    const open = plans.filter((p) => !DONE.has(p.status));
    if (open.length === 0) { toast({ title: "Temizlenecek açık kart yok", description: "Yayında/tamamlanmış kartlara dokunulmaz." }); return; }
    if (!(await confirmDialog({
      title: "Takvim temizlensin mi?",
      description: `${open.length} açık kart silinecek.`,
      lines: ["Yayında ve tamamlanmış kartlar korunur", "Aksiyon planından gelen maddeler plana geri döner"],
      confirmText: "Takvimi Temizle", danger: true,
    }))) return;
    setClearing(true);
    try {
      for (const p of open) await fetch(`/api/content-plans/${p.id}`, { method: "DELETE" }).catch(() => null);
      toast({ title: `${open.length} kart temizlendi` });
      refetchPlans(); refreshPlan();
    } finally { setClearing(false); }
  };

  /* ── sürükle-bırak: içerik taşı VEYA aksiyonu takvime düşür ── */
  const onDragStart = (e: DragStartEvent) => {
    const id = String(e.active.id);
    if (id.startsWith("card-")) setDrag({ type: "card", id: Number(id.slice(5)) });
    else if (id.startsWith("action-")) setDrag({ type: "action", id: id.slice(7) });
    else if (id.startsWith("agentkind-")) setDrag({ type: "agent", kind: id.slice(10) });
  };
  const onDragEnd = async (e: DragEndEvent) => {
    const d = drag;
    setDrag(null);
    const overId = e.over ? String(e.over.id) : null;
    if (!d || !overId || !overId.startsWith("day-")) return;
    const newDate = overId.replace("day-", "");
    if (newDate === "empty") return;

    if (d.type === "card") {
      const p = plans.find((x) => x.id === d.id);
      if (!p || p.plannedDate === newDate) return;
      queryClient.setQueryData<ContentPlan[]>(plansKey, (old) => (old ?? []).map((x) => x.id === d.id ? { ...x, plannedDate: newDate } : x));
      try { await fetch(`/api/content-plans/${d.id}`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify({ plannedDate: newDate }) }); refetchPlans(); }
      catch { toast({ title: "Taşınamadı", variant: "destructive" }); refetchPlans(); }
      return;
    }

    // Ajan koşusu çipi güne bırakıldı: token onayı → tarihli otomatik koşu kartı
    if (d.type === "agent") {
      const chip = AGENT_CHIPS.find((c) => c.kind === d.kind);
      if (!chip || !activeDomainId) return;
      if (newDate < todayStr) { toast({ title: "Geçmiş güne ajan koşusu konmaz", variant: "destructive" }); return; }
      if (!(await confirmDialog({
        title: `${chip.label} planlansın mı?`,
        description: `Kart ${newDate} gününe konur; o sabah 06:00'da ajan kendiliğinden çalışır.`,
        lines: [`Tahmini maliyet: ~${chip.tokens} token (koşu günü harcanır)`],
        confirmText: "Planla",
      }))) return;
      const r = await fetch(`/api/domains/${activeDomainId}/content-plans`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ title: chip.label, contentType: "Ajan koşusu", plannedDate: newDate, agentKind: chip.kind, notes: "Takvimden otomatik koşacak (sabah 06:00)." }),
      }).catch(() => null);
      if (r?.ok) { toast({ title: `${chip.label} planlandı`, description: `${newDate} sabahı otomatik çalışacak.` }); refetchPlans(); }
      else toast({ title: "Planlanamadı", variant: "destructive" });
      return;
    }

    const it = plan?.items.find((x) => x.id === d.id);
    if (!it) return;
    if (await createCardFromAction(it, newDate)) {
      const [, mo, da] = newDate.split("-");
      toast({ title: `Takvime eklendi: ${da} ${TR_MONTHS[Number(mo) - 1]}` });
      refetchPlans(); refreshPlan();
    } else toast({ title: "Takvime eklenemedi", variant: "destructive" });
  };
  const dragCard = drag?.type === "card" ? plans.find((p) => p.id === drag.id) : null;
  const dragAction = drag?.type === "action" ? plan?.items.find((i) => i.id === drag.id) : null;
  const dragAgent = drag?.type === "agent" ? AGENT_CHIPS.find((c) => c.kind === drag.kind) : null;

  /* ── liste sekmesi filtresi ── */
  const listPlans = useMemo(() => {
    let arr = [...plans].sort((a, b) => a.plannedDate.localeCompare(b.plannedDate));
    if (trackFilter === "publish") arr = arr.filter((p) => ["writing", "review", "completed"].includes(p.status));
    if (trackFilter === "revise") arr = arr.filter((p) => !DONE.has(p.status) && (p.status === "revise" || p.contentType === "Revize"));
    if (trackFilter === "overdue") arr = arr.filter((p) => p.plannedDate < todayStr && !DONE.has(p.status));
    return arr;
  }, [plans, trackFilter, todayStr]);

  const selCls = "border rounded-lg px-2 py-1.5 text-[13px] bg-card";

  return (
    <div className="px-4 md:px-6 py-5 mx-auto w-full" style={{ maxWidth: 1760 }}>
      {/* BAŞLIK + AKSİYONLAR */}
      <div className="flex items-end justify-between gap-4 flex-wrap mb-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-brand-500"><Crosshair className="w-4 h-4" /> Komuta Merkezi</div>
          <h1 className="text-3xl font-bold text-foreground mt-0.5">Planlama</h1>
          <p className="text-sm text-muted-foreground">Öncelikler üstte, takvim altta — "Takvime Yerleştir" planı senin için dağıtır.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Strateji seansı: asistan panelini açar, sinyaller okunur, plan birlikte kurulur */}
          <Button onClick={() => assistantStore.ask("Aylık strateji seansı başlatalım. Sinyalleri oku, teşhis koy ve bu ayın planını birlikte kuralım.")}
            className="gap-2 text-white" style={{ background: "#1d1d1b" }}>
            <BrainCircuit className="w-4 h-4" /> Strateji Seansı
          </Button>
          <Button variant="outline" onClick={openAdd} className="border-brand-300 text-brand-600 dark:text-brand-400 gap-2"><Plus className="w-4 h-4" /> Aksiyon Ekle</Button>
          <Button onClick={generate} disabled={planRunning} variant="outline" className="gap-2">
            {planRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {planRunning ? "Uzman düşünüyor…" : plan ? "Planı Yenile" : "Planı Oluştur"}
          </Button>
          <Button onClick={autoPlace} disabled={!plan || placing} className="bg-brand-500 hover:bg-brand-600 gap-2">
            {placing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {placing ? "Yerleştiriliyor…" : "Takvime Yerleştir"}
          </Button>
          <Button onClick={clearCalendar} disabled={clearing} variant="outline" className="gap-2 text-destructive border-destructive/40 hover:text-destructive">
            {clearing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eraser className="w-4 h-4" />}
            {clearing ? "Temizleniyor…" : "Takvimi Temizle"}
          </Button>
        </div>
      </div>

      {!activeDomainId ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">Önce bir domain ekleyip taratman gerekiyor.</CardContent></Card>
      ) : (
        <>
          {/* AJAN DURUM ŞERİDİ + KAPASİTE */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {chips.map((c) => (
              <button key={c.label} onClick={() => setLocation(c.href)} title={`${c.label} sayfasına git`}>
                <StatusChip icon={c.icon} label={c.label} state={c.state} text={c.text} />
              </button>
            ))}
            <span className="inline-flex items-center gap-2 text-[11px] px-2.5 py-1 rounded-full border bg-card">
              <span className="font-medium">Kapasite</span>
              <span className="w-16 h-1.5 rounded-full bg-muted overflow-hidden inline-block">
                <span className="block h-full rounded-full" style={{ width: `${Math.min(100, (stats.contentCount / MONTHLY_CAPACITY) * 100)}%`, background: stats.contentCount > MONTHLY_CAPACITY ? "#d92d20" : "#e83e3c" }} />
              </span>
              <span className="text-muted-foreground">{stats.contentCount}/{MONTHLY_CAPACITY}</span>
            </span>
          </div>

          <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={onDragStart} onDragEnd={onDragEnd}>
            {/* ÜST: uzman kararı + aksiyon kartları ızgarası */}
            <div className="space-y-3 mb-5">
              {plan?.verdict && (
                <Card className="border-brand-200 bg-brand-25 dark:border-brand-500/30 dark:bg-brand-500/[0.06]">
                  <CardContent className="p-4">
                    <div className="text-[10px] font-bold tracking-wide mb-1.5 text-warning-700 dark:text-warning-400">
                      UZMAN KARARI · {new Date(plan.generatedAt).toLocaleString("tr-TR", { day: "numeric", month: "long" })} · {doneCount}/{plan.items.length} tamam
                    </div>
                    <div className="text-[14px] font-semibold leading-snug">{plan.verdict}</div>
                    <div className="text-[12px] text-muted-foreground mt-1.5 line-clamp-2">{plan.summary}</div>
                  </CardContent>
                </Card>
              )}

              {apData?.runError && (
                <Card style={{ borderColor: "#f0443880", background: "#fef2f2" }}><CardContent className="p-3 flex items-center gap-2 text-[12px]" style={{ color: "#b42318" }}>
                  <AlertTriangle className="w-4 h-4 shrink-0" /> Plan üretilemedi: {apData.runError}
                </CardContent></Card>
              )}

              {/* WORDPRESS YAYIN KUYRUĞU: onay bekleyenler + bugüne/geçmişe planlı bekleyenler + hatalılar */}
              {(() => {
                const kuyruk = plans.filter((p) => p.wpYayinModu && p.wpYayinDurumu !== "yayinlandi" && (p.wpYayinDurumu === "onay-bekliyor" || p.wpYayinDurumu === "hata" || p.plannedDate <= todayStr));
                if (!kuyruk.length) return null;
                return (
                  <div className="rounded-xl border p-3.5" style={{ borderColor: "#f7900955", background: "#f7900908" }}>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "#b54708" }}>📤 WordPress Yayın Kuyruğu</span>
                      <span className="text-[11px] text-muted-foreground">— zamanı gelen içerikler; "Yayınla" dediğin an sitede yayına girer</span>
                    </div>
                    <div className="space-y-1.5">
                      {kuyruk.map((p) => (
                        <div key={p.id} className="flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-[12.5px] dark:bg-gray-900 dark:border-white/10">
                          <span className="font-medium truncate flex-1" title={p.title}>{p.title}</span>
                          <span className="text-[11px] text-muted-foreground shrink-0">{p.plannedDate}</span>
                          <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold" style={p.wpYayinDurumu === "hata" ? { background: "#d92d2022", color: "#d92d20" } : p.wpYayinDurumu === "onay-bekliyor" ? { background: "#f7900922", color: "#b54708" } : { background: "#70706c22", color: "#70706c" }}>
                            {p.wpYayinDurumu === "hata" ? "HATA" : p.wpYayinDurumu === "onay-bekliyor" ? "ONAY BEKLİYOR" : p.wpYayinModu === "otomatik" ? "09:00'DA OTOMATİK" : "BEKLİYOR"}
                          </span>
                          <Button size="sm" className="h-7 text-[11px] shrink-0" disabled={wpYayinBusy === p.id} onClick={() => void wpSimdiYayinla(p.id)}>{wpYayinBusy === p.id ? "…" : "Yayınla"}</Button>
                          {p.wpYayinNotu && <span className="text-[10.5px] text-red-600 truncate max-w-[260px]" title={p.wpYayinNotu}>{p.wpYayinNotu}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* ŞEFİN BU HAFTAKİ PLANI (Orkestra Şefi): havuzdan gerekçeli seçim, tek tıkla onay */}
              <div className="rounded-xl border p-3.5" style={{ borderColor: "#e83e3c33", background: "#e83e3c06" }}>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-[11px] font-bold uppercase tracking-wide inline-flex items-center gap-1.5" style={{ color: "#e83e3c" }}>
                    ✨ Geolop Asistanı'nın Bu Haftaki Planı
                  </span>
                  {asistanPlan && <span className="text-[10px] text-muted-foreground">{asistanPlan.haftaBasi} haftası · {asistanPlan.secimler.filter((s) => s.durum === "onerildi").length} onay bekliyor</span>}
                  <Link href="/settings" className="text-[10px] font-semibold rounded-full px-2 py-0.5 border hover:opacity-80" style={{ borderColor: "#e83e3c44", color: "#e83e3c" }} title="Ayarlar > Geolop Asistanı sekmesinden değiştir">
                    {ASISTAN_MOD[asistanAyar?.seviye ?? 0]} modu
                  </Link>
                  <button onClick={() => void asistanPlaniBaslat()} disabled={asistanPlanBusy === "uret"} className="ml-auto text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                    {asistanPlanBusy === "uret" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} {asistanPlanTaze ? "Yeniden Üret" : "Planı Üret"}
                  </button>
                  <span className="text-[10px] text-muted-foreground hidden md:inline">· her pazartesi 08:30'da otomatik</span>
                </div>
                {!asistanPlan ? (
                  <div className="text-[12px] text-muted-foreground">
                    Geolop Asistanı henüz bu hafta için plan üretmedi. Asistan her pazartesi Fırsat Havuzu'ndan haftanın 5-8 işini seçer, gerekçelendirir ve bilerek yapmadıklarını da söyler. İlk planı hemen üretebilirsin (~1 analiz maliyeti).
                  </div>
                ) : (
                  <>
                    <p className="text-[12.5px] leading-relaxed mb-2.5">{asistanPlan.ozet}</p>
                    <div className="space-y-1.5">
                      {asistanPlan.secimler.map((s) => {
                        const kartDurum = s.planId ? asistanPlanVeri?.kartDurumlari?.[s.planId] : undefined;
                        const yapildi = kartDurum === "completed" || kartDurum === "published";
                        return (
                          <div key={s.firsatId} className="flex items-center gap-2 rounded-lg border bg-card px-2.5 py-2 dark:border-white/10">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                <span className="text-[12.5px] font-semibold leading-snug">{s.baslik}</span>
                                <span className="text-[10px] text-muted-foreground">{s.kaynak} · etki {s.etki} · {s.onerilenTarih.slice(5)}</span>
                                {yapildi && <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9.5px] font-bold text-emerald-600 dark:text-emerald-400">YAPILDI</span>}
                                {s.durum === "onaylandi" && !yapildi && <span className="rounded-full bg-blue-500/15 px-1.5 py-0.5 text-[9.5px] font-bold text-blue-600 dark:text-blue-400">TAKVİMDE</span>}
                                {s.durum === "reddedildi" && <span className="rounded-full bg-gray-500/15 px-1.5 py-0.5 text-[9.5px] font-bold text-gray-500">REDDEDİLDİ</span>}
                              </div>
                              <p className="mt-0.5 text-[11px] text-muted-foreground leading-snug">{s.gerekce}</p>
                            </div>
                            {s.durum === "onerildi" && (
                              <div className="flex shrink-0 items-center gap-1">
                                <Button size="sm" className="h-7 text-[11px] gap-1 bg-brand-500 hover:bg-brand-600" disabled={asistanPlanBusy === s.firsatId} onClick={() => void asistanKarar(s, "onayla")}>
                                  {asistanPlanBusy === s.firsatId ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Onayla
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 text-[11px] text-muted-foreground" onClick={() => void asistanKarar(s, "reddet")}>Geç</Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {asistanPlan.yapilmayanlar.length > 0 && (
                      <details className="mt-2.5">
                        <summary className="cursor-pointer text-[11px] font-semibold text-muted-foreground">Asistanın bilerek yapmadıkları ({asistanPlan.yapilmayanlar.length}) — gerekçeleriyle</summary>
                        <ul className="mt-1.5 space-y-1 pl-1">
                          {asistanPlan.yapilmayanlar.map((y, i) => (
                            <li key={i} className="text-[11.5px] text-muted-foreground"><span className="font-medium text-foreground">{y.baslik}:</span> {y.neden}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </>
                )}
                {/* KAZANIM KARNESİ (Faz 5): döngünün kapanışı — dürüst sayılar */}
                {asistanKarne && (asistanKarne.asistan.onerilen > 0 || asistanKarne.havuz.yapilan > 0) && (
                  <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t pt-2.5 text-[11px] text-muted-foreground dark:border-white/10">
                    <span className="font-bold uppercase tracking-wide text-[10px]" style={{ color: "#e83e3c" }}>Kazanım · son {asistanKarne.donem} gün</span>
                    <span>Asistan <b className="text-foreground">{asistanKarne.asistan.onerilen}</b> iş önerdi</span>
                    <span><b className="text-foreground">{asistanKarne.asistan.onaylanan}</b> takvime indi</span>
                    <span><b className="text-foreground">{asistanKarne.asistan.tamamlanan}</b> tamamlandı</span>
                    {asistanKarne.havuz.yapilan > 0 && (
                      <span className="text-emerald-600 dark:text-emerald-400">
                        yapılan {asistanKarne.havuz.yapilan} işin <b>{asistanKarne.havuz.sinyalKapanan}</b>'inde sinyal kapandı ✓ (işe yaradı)
                      </span>
                    )}
                    {asistanKarne.havuz.kendiligindenKapanan > 0 && <span>{asistanKarne.havuz.kendiligindenKapanan} fırsat kendiliğinden kapandı</span>}
                  </div>
                )}
              </div>

              {/* AI ÖNERİLERİ (refleksler): plan vs gerçekleşen sapmaları — tek tıkla kabul */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide inline-flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" style={{ color: "#e83e3c" }} /> AI Önerileri
                    {suggestions.length > 0 && <span className="text-[10px] px-1.5 rounded-full font-bold" style={{ background: "#e83e3c1f", color: "#e83e3c" }}>{suggestions.length}</span>}
                  </span>
                  <button onClick={() => void refreshSuggestions()} disabled={sugRefreshing} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                    {sugRefreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Önerileri Tazele
                  </button>
                  <span className="text-[10px] text-muted-foreground hidden md:inline">· her pazartesi 07:00'de otomatik üretilir</span>
                </div>
                {suggestions.length === 0 ? (
                  <div className="text-[12px] text-muted-foreground border rounded-lg px-3 py-2">Açık öneri yok — plan gerçekleşenle uyumlu. 👍</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
                    {suggestions.map((s) => {
                      const pri = PRI[s.priority] ?? PRI.yuksek;
                      const KIND_TR: Record<string, string> = { gecikme: "Gecikme", "rank-dusus": "Sıralama düşüşü", karne: "Karne", "bayat-denetim": "Bayat denetim" };
                      return (
                        <div key={s.id} className="rounded-lg border p-2.5" style={{ borderColor: `${pri.color}44`, background: `${pri.color}08` }}>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: "#e83e3c14", color: "#e83e3c" }}><Sparkles className="w-2.5 h-2.5" /> AI önerisi</span>
                            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full" style={{ background: pri.bg, color: pri.color }}>{KIND_TR[s.kind] ?? s.kind}</span>
                            {s.suggestedDate && <span className="text-[9px] text-muted-foreground inline-flex items-center gap-0.5"><CalendarDays className="w-2.5 h-2.5" />{s.suggestedDate.slice(5)}</span>}
                          </div>
                          <div className="text-[13px] font-semibold mt-1 leading-snug line-clamp-2">{s.title}</div>
                          <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{s.why}</div>
                          <div className="flex items-center gap-1.5 mt-2">
                            <Button size="sm" className="h-7 text-[11px] gap-1 bg-brand-500 hover:bg-brand-600" disabled={sugBusy === s.id} onClick={() => void acceptSuggestion(s)}>
                              {sugBusy === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Kabul
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-[11px] text-muted-foreground" onClick={() => void dismissSuggestion(s)}>Yoksay</Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {formOpen && (
                <Card className="border-brand-200 dark:border-brand-500/30">
                  <CardContent className="p-4 space-y-2.5">
                    <div className="font-semibold text-sm">{editId ? "Aksiyonu Düzenle" : "Yeni Aksiyon"}</div>
                    <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Aksiyon başlığı" className="w-full border rounded-lg px-3 py-2 text-[13px] bg-card" autoFocus />
                    <textarea value={form.why} onChange={(e) => setForm({ ...form, why: e.target.value })} placeholder="Neden / not (opsiyonel)" className="w-full border rounded-lg px-3 py-2 text-[13px] min-h-[52px] bg-card" />
                    <div className="flex flex-wrap gap-2 items-center">
                      <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={selCls}>
                        <option value="kritik">Kritik</option><option value="yuksek">Yüksek</option><option value="orta">Orta</option>
                      </select>
                      <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={selCls}>
                        <option value="link-insasi">Link İnşası</option><option value="trafik">Trafik</option><option value="teknik">Teknik</option><option value="geo">GEO / AI</option><option value="icerik">İçerik</option>
                      </select>
                      <select value={form.effort} onChange={(e) => setForm({ ...form, effort: e.target.value })} className={selCls}>
                        <option value="dusuk">Düşük efor</option><option value="orta">Orta efor</option><option value="yuksek">Yüksek efor</option>
                      </select>
                      <div className="flex-1" />
                      <Button variant="ghost" size="sm" onClick={() => setFormOpen(false)}>Vazgeç</Button>
                      <Button size="sm" onClick={saveForm} disabled={saving || !form.title.trim()} className="bg-brand-500 hover:bg-brand-600 gap-1">
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Kaydet
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {!plan && !planRunning && !apData?.runError && (
                <Card><CardContent className="p-6 text-center text-muted-foreground text-[13px]">
                  Henüz plan yok. "Planı Oluştur"a bas — tüm ajan verileri okunup öncelik sırası çıkarılır (~1 dk).
                </CardContent></Card>
              )}

              {plan && (() => {
                // Takvime atanan maddeler ızgarada GÖSTERİLMEZ — artık takvimde yaşarlar
                const gridItems = plan.items.filter((it) => !it.dueDate);
                const scheduledCount = plan.items.length - gridItems.length;
                return (
                <>
                  <div className="text-[11px] text-muted-foreground font-medium px-1 flex items-center gap-1.5 flex-wrap">
                    <GripVertical className="w-3.5 h-3.5" /> Maddeyi takvimde bir güne sürükle ya da "Takvime Yerleştir" ile hepsini otomatik dağıt
                    {scheduledCount > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-medium" style={{ color: "#e83e3c", borderColor: "#e83e3c55" }}>
                        <CalendarDays className="w-3 h-3" /> {scheduledCount} madde takvimde
                      </span>
                    )}
                  </div>
                  {gridItems.length === 0 && (
                    <Card><CardContent className="p-5 text-center text-muted-foreground text-[13px]">
                      Tüm maddeler takvime yerleştirildi 🎯 — işleri aşağıdaki takvimden takip et. Kart yayınlanınca madde kendiliğinden tamamlanır.
                    </CardContent></Card>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2.5">
                    {gridItems.map((it, i) => (
                      <DraggableAction key={it.id ?? i} it={it}>
                        <div className="group relative h-full">
                          <ActionCardBody it={it} first={i === 0} />
                          <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-card/90 rounded-md px-0.5">
                            <button onClick={() => void toggleDone(it)} title={it.done ? "Geri al" : "Tamamlandı"} className="p-1 rounded hover:bg-muted"
                              style={{ color: it.done ? "#16a34a" : "var(--muted-foreground)" }}><Check className="w-3.5 h-3.5" /></button>
                            <button onClick={() => setLocation(it.path)} title="İlgili sayfaya git" className="p-1 rounded hover:bg-muted text-muted-foreground"><ArrowRight className="w-3.5 h-3.5" /></button>
                            <button onClick={() => openEdit(it)} title="Düzenle" className="p-1 rounded hover:bg-muted text-muted-foreground"><Pencil className="w-3.5 h-3.5" /></button>
                            <button onClick={() => void removeItem(it)} title="Sil" className="p-1 rounded hover:bg-muted text-muted-foreground"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                      </DraggableAction>
                    ))}
                  </div>
                </>
                );
              })()}
            </div>

            {/* ALT: ajan koşusu paleti + takip şeridi + sekmeler + takvim/liste */}
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-muted-foreground font-medium inline-flex items-center gap-1">
                  <Bot className="w-3.5 h-3.5" /> Ajan koşuları — güne sürükle, o sabah otomatik çalışır:
                </span>
                {AGENT_CHIPS.map((c) => <DraggableAgentChip key={c.kind} c={c} />)}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {([
                  ["publish", "Yayın kuyruğu", Send, "#f79009", queues.publish.length, "yazılıyor · incelemede · yayın bekliyor"],
                  ["revise", "Revize kuyruğu", RefreshCw, "#ec6461", queues.revise.length, "revize durumundaki + Revize tipli işler"],
                  ["overdue", "Geciken", AlertTriangle, "#d92d20", queues.overdue.length, "tarihi geçmiş, bitmemiş işler"],
                ] as const).map(([key, label, Icon, color, count, hint]) => (
                  <button key={key} onClick={() => { setTab("list"); setTrackFilter(trackFilter === key ? null : key); }}
                    className="rounded-xl border p-3 text-left transition-all hover:shadow-sm"
                    style={{ borderColor: trackFilter === key ? color : "var(--border)", background: trackFilter === key ? `${color}0d` : "var(--card)" }}>
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-medium flex items-center gap-1.5" style={{ color }}><Icon className="w-3.5 h-3.5" />{label}</span>
                      <span className="text-xl font-bold" style={{ color: count > 0 ? color : "var(--muted-foreground)" }}>{count}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1 hidden md:block">{hint}</div>
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-1 border-b">
                {([["calendar", "Takvim", CalendarDays], ["list", "Liste", ListChecks]] as const).map(([k, label, Icon]) => (
                  <button key={k} onClick={() => { setTab(k); if (k !== "list") setTrackFilter(null); }}
                    className="px-4 py-2.5 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition-colors"
                    style={{ borderColor: tab === k ? "#ec6461" : "transparent", color: tab === k ? "#ec6461" : "var(--muted-foreground)" }}>
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                ))}
                <div className="flex-1" />
                <div className="text-[11px] text-muted-foreground pb-1 hidden lg:block">
                  Bu ay: <b>{stats.total}</b> iş · <b>{stats.done}</b> tamam · %{stats.progress}
                </div>
              </div>

              {tab === "calendar" && (
                <Card><CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
                      <div className="font-semibold text-lg min-w-[150px] text-center">{TR_MONTHS[viewMonth]} {viewYear}</div>
                      <Button variant="ghost" size="icon" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
                      <Button variant="outline" size="sm" className="ml-1" onClick={() => { setViewYear(today.getFullYear()); setViewMonth(today.getMonth()); }}>Bugün</Button>
                    </div>
                    <label className="flex items-center gap-1.5 cursor-pointer text-xs text-muted-foreground"><input type="checkbox" checked={dimPast} onChange={(e) => setDimPast(e.target.checked)} /> geçmiş günler mat</label>
                  </div>
                  <div className="grid grid-cols-7 gap-1.5 mb-1.5">{TR_DAYS.map((d) => <div key={d} className="text-center text-xs font-medium text-muted-foreground py-1">{d.slice(0, 3)}</div>)}</div>
                  <div className="grid grid-cols-7 gap-1.5">
                    {cells.map((date, i) => (
                      <DayCell key={i} date={date} todayStr={todayStr}
                        items={date ? (byDate.get(ymd(date)) ?? []) : []}
                        isToday={!!date && ymd(date) === todayStr}
                        dim={!!date && dimPast && ymd(date) < todayStr}
                        onOpen={setSelected} />
                    ))}
                  </div>
                </CardContent></Card>
              )}

              {tab === "list" && (
                <Card><CardContent className="p-0">
                  {trackFilter && (
                    <div className="px-4 py-2 border-b text-[12px] flex items-center gap-2">
                      <span className="text-muted-foreground">Filtre:</span>
                      <b>{trackFilter === "publish" ? "Yayın kuyruğu" : trackFilter === "revise" ? "Revize kuyruğu" : "Geciken"}</b>
                      <button onClick={() => setTrackFilter(null)} className="text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-xs text-muted-foreground text-left">
                      <th className="p-3 font-medium">Başlık</th><th className="p-3 font-medium">Tarih</th><th className="p-3 font-medium">Kategori</th>
                      <th className="p-3 font-medium">Yazar</th><th className="p-3 font-medium">Tip</th><th className="p-3 font-medium">Durum</th><th className="p-3 font-medium text-right">Hacim</th>
                    </tr></thead>
                    <tbody>
                      {listPlans.map((p) => {
                        const st = STATUS[p.status] ?? STATUS.planned; const ty = typeOf(p.contentType);
                        const [, mo, da] = p.plannedDate.split("-");
                        const overdue = p.plannedDate < todayStr && !DONE.has(p.status);
                        return (
                          <tr key={p.id} onClick={() => setSelected(p)} className="border-b hover:bg-muted/30 cursor-pointer" style={{ borderLeft: `3px solid ${ty.accent}` }}>
                            <td className="p-3 font-medium">{overdue && <AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5 text-error-500" />}{p.title}</td>
                            <td className="p-3 text-muted-foreground">{da} {TR_MONTHS[Number(mo) - 1]?.slice(0, 3)}</td>
                            <td className="p-3 text-muted-foreground">{p.category ?? "—"}</td>
                            <td className="p-3 text-muted-foreground">{p.author ?? "—"}</td>
                            <td className="p-3"><span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: `${ty.accent}1f`, color: ty.accent }}>{p.contentType}</span></td>
                            <td className="p-3"><span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: `${st.color}22`, color: st.color }}>{st.label}</span></td>
                            <td className="p-3 text-right text-muted-foreground">{fmtK(volOf(p))}</td>
                          </tr>
                        );
                      })}
                      {listPlans.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">{trackFilter ? "Bu kuyrukta iş yok. 👍" : "Henüz içerik yok — kartlar içerik ajanından ve aksiyon planından gelir."}</td></tr>}
                    </tbody>
                  </table>
                </CardContent></Card>
              )}
            </div>

            <DragOverlay>
              {dragCard ? <div className="w-[200px]"><MiniCard p={dragCard} todayStr={todayStr} ghost /></div>
                : dragAction ? <div className="w-[280px]"><ActionCardBody it={dragAction} ghost /></div>
                : dragAgent ? <span className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-full border bg-card shadow-lg" style={{ borderColor: "#d97706", borderStyle: "dashed" }}><Bot className="w-3.5 h-3.5" style={{ color: "#d97706" }} /> {dragAgent.label}</span>
                : null}
            </DragOverlay>
          </DndContext>
        </>
      )}

      {/* DETAY DRAWER (mevcut kartlar) */}
      <Sheet open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          {draft && (draft.contentType === "Görev" || draft.contentType === "Ajan koşusu" ? (
            /* GÖREV / AJAN KOŞUSU ÇEKMECESİ — içerik alanları (keyword/final link/teslim) YOK;
               sade durum akışı + not + ilgili ajan kısayolu. Ajan koşusunda sorumlu da yok. */
            <div className="space-y-4">
              <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} onBlur={(e) => save({ title: e.target.value })} placeholder="Görev başlığı" className="text-base font-semibold border-0 px-0 pr-8 focus-visible:ring-0" />
              <div className="flex gap-2 flex-wrap">
                <span className="text-xs px-2 py-1 rounded" style={{ background: `${(STATUS[draft.status] ?? STATUS.planned).color}22`, color: (STATUS[draft.status] ?? STATUS.planned).color }}>
                  {GOREV_STATUS.find(([k]) => k === draft.status)?.[1] ?? (STATUS[draft.status] ?? STATUS.planned).label}
                </span>
                <span className="text-xs px-2 py-1 rounded inline-flex items-center gap-1" style={{ background: `${typeOf(draft.contentType).accent}22`, color: typeOf(draft.contentType).accent }}>
                  {draft.contentType === "Ajan koşusu" ? <Bot className="w-3 h-3" /> : <ClipboardCheck className="w-3 h-3" />} {draft.contentType}
                </span>
                {draft.actionItemId && <span className="text-xs px-2 py-1 rounded" style={{ background: "#e83e3c14", color: "#e83e3c" }}>aksiyon planından</span>}
              </div>
              {draft.agentKind && draft.status === "planned" && (
                <div className="text-[12px] rounded-lg px-3 py-2" style={{ background: "rgba(217,119,6,0.08)", color: "#b45309" }}>
                  🤖 Bu kart planlı tarihin sabahı (06:00) ilgili ajanı kendiliğinden çalıştırır. Günü değiştirmek koşuyu da erteler.
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Durum">
                  <Select value={GOREV_STATUS.some(([k]) => k === draft.status) ? draft.status : "planned"} onValueChange={(v) => save({ status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{GOREV_STATUS.map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Planlı tarih"><Input type="date" value={draft.plannedDate} onChange={(e) => save({ plannedDate: e.target.value })} /></Field>
              </div>
              {!draft.agentKind && (
                <Field label="Sorumlu"><Input list="pl-authors" value={draft.author ?? ""} onChange={(e) => setDraft({ ...draft, author: e.target.value })} onBlur={(e) => save({ author: e.target.value || null })} placeholder="Kim yapacak?" /><datalist id="pl-authors">{authors.map((a) => <option key={a} value={a} />)}</datalist></Field>
              )}
              <Field label="Not / neden"><Textarea value={draft.notes ?? ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} onBlur={(e) => save({ notes: e.target.value || null })} rows={6} /></Field>
              {(() => {
                const AGENT_PATHS: Record<string, string> = { "tech-audit": "/tech-audit", backlink: "/backlink-audit", "ai-visibility": "/traffic-analysis", "rank-tracking": "/rank-tracking", "seo-report": "/seo-report" };
                const path = draft.agentKind ? AGENT_PATHS[draft.agentKind] : plan?.items.find((i) => i.id === draft.actionItemId)?.path;
                return path ? (
                  <Button variant="outline" className="w-full gap-2 border-brand-300 text-brand-600 dark:text-brand-400" onClick={() => { setSelected(null); setLocation(path); }}>
                    <ArrowRight className="w-4 h-4" /> İlgili ajan sayfasına git
                  </Button>
                ) : null;
              })()}
              <div className="pt-2"><Button variant="ghost" className="text-destructive hover:text-destructive gap-2" onClick={removePlanCard}><Trash2 className="w-4 h-4" /> Kartı sil</Button></div>
            </div>
          ) : (
            <div className="space-y-4">
              {(draft.coverUrl || /\/content\/(\d+)/.test(draft.notes ?? "")) && (
                <div className="rounded-lg overflow-hidden border" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
                  {draft.coverUrl && <img src={draft.coverUrl} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full object-cover" style={{ maxHeight: 150 }} />}
                  {(() => { const m = (draft.notes ?? "").match(/\/content\/(\d+)/); return m ? (
                    <Link href={`/content/${m[1]}`}><Button size="sm" variant="outline" className="m-2 text-xs">📄 Üretilen içeriği aç →</Button></Link>
                  ) : null; })()}
                </div>
              )}
              <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} onBlur={(e) => save({ title: e.target.value })} placeholder="Başlık" className="text-base font-semibold border-0 px-0 pr-8 focus-visible:ring-0" />
              <div className="flex gap-2">
                <span className="text-xs px-2 py-1 rounded" style={{ background: `${(STATUS[draft.status] ?? STATUS.planned).color}22`, color: (STATUS[draft.status] ?? STATUS.planned).color }}>{(STATUS[draft.status] ?? STATUS.planned).label}</span>
                <span className="text-xs px-2 py-1 rounded" style={{ background: `${typeOf(draft.contentType).accent}22`, color: typeOf(draft.contentType).accent }}>{draft.contentType}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Durum"><Select value={draft.status} onValueChange={(v) => save({ status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STATUS_KEYS.map((k) => <SelectItem key={k} value={k}>{STATUS[k].label}</SelectItem>)}</SelectContent></Select></Field>
                <Field label="Tip"><Select value={draft.contentType} onValueChange={(v) => save({ contentType: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></Field>
                <Field label="Yazar"><Input list="pl-authors" value={draft.author ?? ""} onChange={(e) => setDraft({ ...draft, author: e.target.value })} onBlur={(e) => save({ author: e.target.value || null })} placeholder="Yazar" /><datalist id="pl-authors">{authors.map((a) => <option key={a} value={a} />)}</datalist></Field>
                <Field label="Kategori"><Input list="pl-cats" value={draft.category ?? ""} onChange={(e) => setDraft({ ...draft, category: e.target.value })} onBlur={(e) => save({ category: e.target.value || null })} placeholder="Kategori" /><datalist id="pl-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist></Field>
                <Field label="Planlı tarih"><Input type="date" value={draft.plannedDate} onChange={(e) => save({ plannedDate: e.target.value })} /></Field>
                <Field label="Teslim tarihi"><Input type="date" value={draft.deliveryDate ?? ""} onChange={(e) => save({ deliveryDate: e.target.value || null })} /></Field>
              </div>
              <Field label="Final link (herhangi bir URL)"><div className="flex items-center gap-2"><Input value={draft.finalLink ?? ""} onChange={(e) => setDraft({ ...draft, finalLink: e.target.value })} onBlur={(e) => save({ finalLink: e.target.value || null })} placeholder="https://..." />{draft.finalLink && <a href={draft.finalLink} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 text-muted-foreground" /></a>}</div></Field>
              <div>
                <div className="flex items-center justify-between mb-1.5"><div className="text-sm font-medium">Keyword'ler</div><div className="text-xs text-muted-foreground">Toplam global: {fmtK(draftVol)}</div></div>
                <div className="rounded-lg border divide-y">
                  <div className="grid grid-cols-[1fr_50px_60px_70px] gap-2 px-3 py-1.5 text-[11px] text-muted-foreground"><span>Keyword</span><span className="text-right">USA</span><span className="text-right">Global</span><span className="text-right">Rol</span></div>
                  {(draft.keywords ?? []).map((kw, i) => (
                    <div key={i} className="grid grid-cols-[1fr_50px_60px_70px] gap-2 px-3 py-2 items-center text-sm">
                      <span className="truncate">{kw.keyword}</span><span className="text-right text-muted-foreground">{kw.usa}</span><span className="text-right text-muted-foreground">{kw.global}</span>
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => save({ keywords: draft.keywords.map((x, j) => j === i ? { ...x, role: x.role === "ana" ? "destek" : "ana" } : x) })} className="text-[10px] px-1.5 rounded" style={{ background: kw.role === "ana" ? "#ec646122" : "var(--muted)", color: kw.role === "ana" ? "#ec6461" : undefined }}>{kw.role === "ana" ? "ana" : "dst"}</button>
                        <button onClick={() => save({ keywords: draft.keywords.filter((_, j) => j !== i) })}><X className="w-3 h-3 text-muted-foreground" /></button>
                      </div>
                    </div>
                  ))}
                  <KeywordAdder onAdd={(k) => save({ keywords: [...(draft.keywords ?? []), k] })} />
                </div>
              </div>
              <Field label="Not"><Textarea value={draft.notes ?? ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} onBlur={(e) => save({ notes: e.target.value || null })} rows={3} placeholder="İç linkler güncellenecek, schema eksik…" /></Field>
              {draft.sourceUrl && <div className="text-xs text-muted-foreground">Kaynak öneri: {draft.sourceUrl}{draft.anchorText ? ` ("${draft.anchorText}")` : ""}</div>}
              <div className="pt-2"><Button variant="ghost" className="text-destructive hover:text-destructive gap-2" onClick={removePlanCard}><Trash2 className="w-4 h-4" /> Sil</Button></div>
            </div>
          ))}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-1"><div className="text-xs text-muted-foreground">{label}</div>{children}</div>;
}
function KeywordAdder({ onAdd }: { onAdd: (k: ContentKeyword) => void }) {
  const [kw, setKw] = useState(""); const [usa, setUsa] = useState(""); const [gl, setGl] = useState("");
  const add = () => { if (!kw.trim()) return; onAdd({ keyword: kw.trim(), usa: Number(usa) || 0, global: Number(gl) || 0, role: "destek" }); setKw(""); setUsa(""); setGl(""); };
  return (
    <div className="grid grid-cols-[1fr_50px_60px_36px] gap-2 px-3 py-2 items-center">
      <Input value={kw} onChange={(e) => setKw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="yeni keyword" className="h-8 text-sm" />
      <Input value={usa} onChange={(e) => setUsa(e.target.value)} placeholder="USA" className="h-8 text-sm px-1 text-right" />
      <Input value={gl} onChange={(e) => setGl(e.target.value)} placeholder="Gl." className="h-8 text-sm px-1 text-right" />
      <Button size="icon" className="h-8 w-8" onClick={add}><Plus className="w-4 h-4" /></Button>
    </div>
  );
}
